import { AdhocMessage } from '@/models/AdhocMessage';
import {
  AuthResponseBody,
  CreateThreadResponseBody,
  ForkThreadResponseBody,
  GetLastEventOfTypeResponseBody,
  GetMessageMetadataResponseBody,
  GetThreadProfileResponseBody,
  GetThreadProfileToolConfigResponseBody,
  GetThreadResponseBody,
  GetUserThreadsResponseBody,
  GetWidgetByIdResponseBody,
  SaveDataContributionConsentResponseBody,
  SaveThreadFeedbackResponseBody,
  SaveThreadReactionResponseBody,
  SendMessageResponseBody,
} from '@/api/ApiResponse';
import { BaseApi } from '@/api/BaseApi';
import { EventType, InteractionEvent } from '@/models/Interaction';
import { Evidence } from '@/@types/Evidence';
import { getAppConfig } from '@/utils/config';
import { getLayoverLogger } from '@/logging/LayoverLogger';
import { MessageId, NewMessage } from '@/models/Message';
import { Reaction } from '@/models/Reaction';
import { StoreEventFromApi } from '@/models/Event';
import { ThreadId } from '@/models/Thread';

const CHAT_URL_PREFIX = '/api/chat';
const REST_URL_PREFIX = '/api/rest';

export const READER_BACKEND_HOST = getAppConfig(
  (_) => _.reader.backend_host,
  'https://mage.allen.ai',
);

export class ChatAPI extends BaseApi {
  async authenticate() {
    return this.request<AuthResponseBody>({
      url: `${CHAT_URL_PREFIX}/auth`,
      method: 'POST',
      body: {},
    });
  }

  async login(authUser: {
    anonymousUserId: string;
    auth0UserId: string;
    email: string;
    nickname?: string;
    name?: string;
  }) {
    return this.request<AuthResponseBody>({
      url: `${CHAT_URL_PREFIX}/login`,
      method: 'POST',
      body: {
        anonymous_user_id: authUser.anonymousUserId,
        auth0_user_id: authUser.auth0UserId,
        email: authUser.email,
        nickname: authUser.nickname,
        name: authUser.name,
      },
    });
  }

  async createThread() {
    return this.request<CreateThreadResponseBody>({
      url: `${CHAT_URL_PREFIX}/thread`,
      method: 'PUT',
      body: {},
    });
  }

  async getUserThreads() {
    return this.request<GetUserThreadsResponseBody>({
      url: `${CHAT_URL_PREFIX}/thread/all`,
      method: 'GET',
    });
  }

  async getThread(threadId: string) {
    return this.request<GetThreadResponseBody>({
      url: `${CHAT_URL_PREFIX}/thread/${threadId}`,
      method: 'GET',
    });
  }

  async getThreadProfile(threadId: string) {
    return this.request<GetThreadProfileResponseBody>({
      url: `${REST_URL_PREFIX}/thread/${threadId}/profile`,
      method: 'GET',
    });
  }

  async getThreadProfileToolConfig(threadId: string, toolName: string) {
    return this.request<GetThreadProfileToolConfigResponseBody>({
      url: `${REST_URL_PREFIX}/thread/${threadId}/profile/tools/${toolName}/config`,
      method: 'GET',
    });
  }

  async archiveThread(threadId: string) {
    return this.request({
      url: `${CHAT_URL_PREFIX}/thread/${threadId}`,
      method: 'PATCH',
    });
  }

  async sendMessage(message: NewMessage, handlerProfile: string | null) {
    return this.request<SendMessageResponseBody>({
      url: `${CHAT_URL_PREFIX}/message`,
      method: 'POST',
      body: {
        text: message.text,
        thread_id: message.threadId,
        profile: handlerProfile,
      },
    });
  }

  async saveMessageReaction({
    messageId,
    reaction,
  }: {
    messageId: MessageId;
    reaction: Reaction | null;
  }) {
    return this.request<SendMessageResponseBody>({
      url: `${CHAT_URL_PREFIX}/message/${messageId}/reaction`,
      method: 'POST',
      body: { reaction },
    });
  }

  async saveMessageFeedback({
    messageId,
    feedback,
  }: {
    messageId: MessageId;
    feedback: string;
  }) {
    return this.request<SendMessageResponseBody>({
      url: `${CHAT_URL_PREFIX}/message/${messageId}/feedback`,
      method: 'POST',
      body: { feedback },
    });
  }

  async saveThreadFeedback({
    threadId,
    feedback,
  }: {
    threadId: ThreadId;
    feedback: string;
  }) {
    return this.request<SaveThreadFeedbackResponseBody>({
      url: `${CHAT_URL_PREFIX}/thread/${threadId}/feedback`,
      method: 'POST',
      body: { feedback },
    });
  }

  async saveThreadReaction({
    threadId,
    reaction,
  }: {
    threadId: ThreadId;
    reaction: Reaction | null;
  }) {
    return this.request<SaveThreadReactionResponseBody>({
      url: `${CHAT_URL_PREFIX}/thread/${threadId}/reaction`,
      method: 'POST',
      body: { reaction },
    });
  }

  async getMessageMetadata({ messageId }: { messageId: MessageId }) {
    return this.request<GetMessageMetadataResponseBody>({
      url: `${CHAT_URL_PREFIX}/message/${messageId}/metadata`,
      method: 'GET',
    });
  }

  async forkThreadFromMessage(messageId: MessageId, eventId?: string) {
    return this.request<ForkThreadResponseBody>({
      url: `${CHAT_URL_PREFIX}/thread/fork`,
      method: 'POST',
      body: { message_id: messageId, event_id: eventId },
    });
  }

  async saveDataContributionConsent(consent: boolean) {
    return this.request<SaveDataContributionConsentResponseBody>({
      url: `${CHAT_URL_PREFIX}/channel/consent`,
      method: 'POST',
      body: { data_contribution_consent: consent },
    });
  }

  // logs event to interactions store
  async trackEvent(data: InteractionEvent) {
    return this.request({
      url: `${CHAT_URL_PREFIX}/event`,
      method: 'POST',
      body: {
        event_type: data.type,
        text: data.text,
        data: data.data,
        message_id: data.messageId,
        thread_id: data.threadId,
        channel_id: data.channelId,
      },
    });
  }

  async getLatestEventOfType(threadId: string, eventType: string) {
    return this.request<GetLastEventOfTypeResponseBody>({
      url: `${REST_URL_PREFIX}/thread/${threadId}/event/${eventType}`,
      method: 'GET',
    });
  }

  async sendAdhocMessage(adhocMsg: AdhocMessage) {
    return this.request<SendMessageResponseBody>({
      url: `${CHAT_URL_PREFIX}/message/adhoc`,
      method: 'PUT',
      body: {
        text: adhocMsg.text,
        actor_id: adhocMsg.actorId,
        metadata: adhocMsg.metadata,
      },
    });
  }

  async getEventById(eventId: string) {
    return this.request<StoreEventFromApi>({
      url: `${REST_URL_PREFIX}/event/${eventId}`,
      method: 'GET',
    });
  }

  async getEventByWidgetId({
    widgetEventType,
    threadId,
    widgetId,
  }: {
    widgetEventType: EventType;
    threadId: string;
    widgetId: string;
  }) {
    return this.request<GetWidgetByIdResponseBody>({
      url: `${REST_URL_PREFIX}/thread/${threadId}/widget/${widgetEventType}/${widgetId}`,
      method: 'GET',
    });
  }
}

let chatAPI: ChatAPI | undefined;
export function getChatAPI(): ChatAPI {
  if (!chatAPI) {
    chatAPI = new ChatAPI();
  }
  return chatAPI;
}

export async function getTopicDescription(
  topicId: string | number,
): Promise<null | string> {
  try {
    const response = await fetch(
      `https://topics-beta.apps.semanticscholar.org/api/topic/${topicId}`,
    );
    const data = await response.json();
    const newDescription = data?.papers?.[0]?.description?.general;
    if (typeof newDescription === 'string') {
      return newDescription;
    }
    return null;
  } catch (error) {
    console.error('Error parsing or fetching topic description', error);
    return null;
  }
}

export async function isCorpusIdSearchable(
  corpusId: string | number,
): Promise<boolean> {
  try {
    const response = await fetch(
      `${REST_URL_PREFIX}/searchable/${corpusId}/has-pdf`,
    );
    if (response.status === 200) {
      const data = await response.json();
      return data;
    } else {
      console.error('Error fetching searchable with pdf', corpusId);
      return false;
    }
  } catch (error) {
    console.error('Error fetching searchable with pdf', corpusId, error);
    return false;
  }
}

export async function getSearchableUrl(
  corpusId: string | number,
): Promise<string | null> {
  try {
    const response = await fetch(`${REST_URL_PREFIX}/searchable/${corpusId}`);
    if (response.status !== 200) {
      return null;
    }

    const data = await response.json();
    if (!data.searchable) {
      return null;
    }

    for (const pdf of data.pdfs) {
      if (pdf.url_self_hosted || pdf.url) {
        return pdf.url_self_hosted || pdf.url;
      }
    }
    return null;
  } catch (error) {
    getLayoverLogger().logError(
      'Error fetching searchable url',
      error as object,
    );
    return null;
  }
}

export function getShowableUrlWithoutChecking(
  corpusId: number | string,
): string {
  return `${READER_BACKEND_HOST}/pdf/${corpusId}`;
}

export function getEvidence(evidenceId: number) {
  return fetch(`${READER_BACKEND_HOST}/evidence/${evidenceId}`).then(
    (response) =>
      response.json() as unknown as {
        supports: Evidence[];
        corpusId: number;
      },
  );
}
